import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, Flex, SimpleGrid } from '@chakra-ui/react';

import { useAlert, useApi, useAutocomplete } from '@app/hooks';
import Dialog from '@app/components/common/modals/dialog';
import AutocompleteField from '@app/components/common/autocomplete-field';
import { SelectField, SwitchField, TextField, Txt } from '@app/components/common';
import { TableBase } from '@app/components/common/table/table-base';
import { TableColumn } from '@app/components/common/table';
import { CategorySku, Sku, SubcategorySku, UnitSku } from '@app/api/models/sku';
import { useQueryTable } from '@app/components/common/table/query-table';

import { DistributorSkuRow } from './columns';

const UNIT_OPTIONS: EntityBase[] = [
  { id: 1, name: 'Штуки' },
  { id: 2, name: 'Коробки' },
];

type Draft = {
  id?: number;
  distributor?: EntityBase;
  distributor_sku_code: string;
  distributor_sku_title: string;
  distributor_bar_code: string;
  distributor_ean_code: string;
  distributor_unit?: EntityBase;
  is_active: boolean;
  sku_id: number | null;
};

type SearchDraft = {
  bar_code: string;
  erp_code: string;
  code: string;
  title: string;
  category?: EntityBase;
  subcategory?: EntityBase;
};

const emptyDraft = (): Draft => ({
  distributor_sku_code: '',
  distributor_sku_title: '',
  distributor_bar_code: '',
  distributor_ean_code: '',
  is_active: true,
  sku_id: null,
});

const emptySearch = (): SearchDraft => ({
  bar_code: '',
  erp_code: '',
  code: '',
  title: '',
});

const unitFromValue = (value?: string | null) =>
  UNIT_OPTIONS.find((item) => item.name === value || String(item.id) === value);

type EditDistributorSkuProps = {
  open: boolean;
  initial?: DistributorSkuRow;
  onClose: () => void;
};

export const EditDistributorSku: FC<EditDistributorSkuProps> = ({
  open,
  initial,
  onClose,
}) => {
  const api = useApi();
  const { error, success } = useAlert();
  const { refetch } = useQueryTable();
  const getAutocomplete = useAutocomplete();

  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState<Draft>(emptyDraft());
  const [selectedSku, setSelectedSku] = useState<Sku | null>(null);
  const [search, setSearch] = useState<SearchDraft>(emptySearch());
  const [searchLoading, setSearchLoading] = useState(false);
  const [foundSkus, setFoundSkus] = useState<Sku[]>([]);
  const [categories, setCategories] = useState<CategorySku[]>([]);
  const [subcategories, setSubcategories] = useState<SubcategorySku[]>([]);
  const [units, setUnits] = useState<UnitSku[]>([]);

  const unitsById = useMemo(
    () => units.reduce<Record<number, string>>((acc, item) => {
      acc[item.id] = item.name;
      return acc;
    }, {}),
    [units]
  );

  const subcategoryOptions = useMemo(
    () => subcategories
      .filter((item) => !search.category || item.category_id === Number(search.category.id))
      .map((item) => ({ id: item.id, name: item.name })),
    [search.category, subcategories]
  );

  const patchDraft = (value: Partial<Draft>) => setDraft((prev) => ({ ...prev, ...value }));
  const patchSearch = (value: Partial<SearchDraft>) => setSearch((prev) => ({ ...prev, ...value }));

  useEffect(() => {
    if (!open) {
      return;
    }

    (async () => {
      const [categoriesRes, subcategoriesRes, unitsRes] = await Promise.all([
        api.sku.getCategories(true),
        api.sku.getSubcategories(true),
        api.sku.getUnits(true),
      ]);
      setCategories(categoriesRes?.data || []);
      setSubcategories(subcategoriesRes?.data || []);
      setUnits(unitsRes?.data || []);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    setSearch(emptySearch());
    setFoundSkus([]);

    if (!initial) {
      setDraft(emptyDraft());
      setSelectedSku(null);
      return;
    }

    setDraft({
      id: initial.id,
      distributor: initial.distributor_id
        ? {
          id: initial.distributor_id,
          name: initial.distributor_name
            ? `[${initial.distributor_id}] ${initial.distributor_name}`
            : String(initial.distributor_id),
        }
        : undefined,
      distributor_sku_code: initial.distributor_sku_code || '',
      distributor_sku_title: initial.distributor_sku_title || '',
      distributor_bar_code: initial.distributor_bar_code || '',
      distributor_ean_code: initial.distributor_ean_code || '',
      distributor_unit: unitFromValue(initial.distributor_unit),
      is_active: initial.is_active,
      sku_id: initial.sku_id ?? null,
    });

    if (initial.sku_data) {
      setSelectedSku({
        id: initial.sku_data.id,
        title: initial.sku_data.title,
        code: initial.sku_data.code,
        erp_code: initial.sku_data.erp_code,
        bar_code: initial.sku_data.bar_code,
        ean_code: initial.sku_data.ean_code,
        unit_id: initial.sku_data.unit_id,
        is_active: initial.sku_data.is_active ?? true,
      });
      return;
    }

    if (!initial.sku_id) {
      setSelectedSku(null);
      return;
    }

    (async () => {
      const response = await api.sku.getSkus({
        limit: 1,
        offset: 0,
        sort: '+id',
        filter: { pid: initial.sku_id },
      });
      setSelectedSku(response?.data?.data?.[0] || null);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initial]);

  const onSearch = useCallback(async () => {
    setSearchLoading(true);
    const response = await api.sku.getSkus({
      limit: 50,
      offset: 0,
      sort: '+id',
      filter: {
        ...(search.bar_code ? { bar_code: search.bar_code } : {}),
        ...(search.erp_code ? { erp_code: search.erp_code } : {}),
        ...(search.code ? { code: search.code } : {}),
        ...(search.title ? { title: search.title } : {}),
        ...(search.category ? { category_id: [Number(search.category.id)] } : {}),
        ...(search.subcategory ? { subcategory_id: [Number(search.subcategory.id)] } : {}),
        is_active: true,
      },
    });
    setFoundSkus(response?.data?.data || []);
    setSearchLoading(false);
  }, [api, search]);

  const onMatchSku = useCallback((sku: Sku) => {
    setSelectedSku(sku);
    patchDraft({ sku_id: sku.id });
  }, []);

  const onClearMatch = useCallback(() => {
    setSelectedSku(null);
    patchDraft({ sku_id: null });
  }, []);

  const onSave = async () => {
    if (!draft.distributor?.id) {
      error('Выберите дистрибьютора');
      return;
    }
    if (!draft.distributor_sku_code.trim()) {
      error('Введите код SKU');
      return;
    }
    if (!draft.distributor_sku_title.trim()) {
      error('Введите название SKU');
      return;
    }

    const payload = {
      distributor_id: Number(draft.distributor.id),
      distributor_sku_code: draft.distributor_sku_code.trim(),
      distributor_sku_title: draft.distributor_sku_title.trim(),
      distributor_bar_code: draft.distributor_bar_code.trim() || undefined,
      distributor_ean_code: draft.distributor_ean_code.trim() || undefined,
      distributor_unit: draft.distributor_unit?.name,
      sku_id: draft.sku_id,
      is_active: draft.is_active,
    };

    setLoading(true);
    try {
      if (draft.id) {
        const response = await api.sku.updateDistributorSku({
          ...payload,
          id: draft.id,
        });
        if (response?.code === 200) {
          success('Сопоставление обновлено');
          refetch();
          onClose();
        }
      } else {
        const response = await api.sku.createDistributorSku(payload);
        if (response?.code === 200 || response?.code === 201) {
          success('Сопоставление создано');
          refetch();
          onClose();
        }
      }
    } catch (e) {
      error(e.message);
    } finally {
      setLoading(false);
    }
  };

  const foundColumns: TableColumn<Sku>[] = useMemo(() => [
    {
      name: 'id',
      label: 'ID',
      options: { filter: false, sort: false },
    },
    {
      name: 'title',
      label: 'Название',
      options: { filter: false, sort: false },
    },
    {
      name: 'erp_code',
      label: 'Код ERP',
      options: { filter: false, sort: false },
    },
    {
      name: 'code',
      label: 'Код',
      options: { filter: false, sort: false },
    },
    {
      name: 'unit_id',
      label: 'Ед.изм.',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value: number) => unitsById[value] || '—',
      },
    },
    {
      name: 'id',
      label: '',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (_id, { rowData }) => (
          <Button
            size="sm"
            colorScheme="brand"
            variant={selectedSku?.id === rowData.id ? 'solid' : 'outline'}
            onClick={() => onMatchSku(rowData)}
          >
            Сопоставить
          </Button>
        ),
      },
    },
  ], [onMatchSku, selectedSku?.id, unitsById]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      loading={loading}
      width="1200px"
      title={draft.id ? 'Редактирование SKU дистрибьютора' : 'Создание SKU дистрибьютора'}
      actions={
        <Flex justifyContent="space-between" w="100%">
          <Button variant="solid" colorScheme="brand" onClick={onSave} isLoading={loading}>
            Сохранить
          </Button>
          <Button onClick={onClose}>Отмена</Button>
        </Flex>
      }
    >
      <Flex gap={6} direction={{ base: 'column', lg: 'row' }} alignItems="stretch">
        <Flex flex={1} direction="column" gap={3} minW={0}>
          <Txt fontWeight={600}>Данные дистрибьютора</Txt>
          <AutocompleteField
            required
            id="distributor"
            label="Дистрибьютор"
            name="distributor"
            valueProperty="name"
            value={draft.distributor}
            onFetchOptions={getAutocomplete}
            onChange={(value) => patchDraft({ distributor: value })}
          />
          <TextField
            required
            id="distributor_sku_code"
            label="Код SKU"
            value={draft.distributor_sku_code}
            onChange={(value) => patchDraft({ distributor_sku_code: value })}
          />
          <TextField
            required
            id="distributor_sku_title"
            label="Название SKU"
            value={draft.distributor_sku_title}
            onChange={(value) => patchDraft({ distributor_sku_title: value })}
          />
          <TextField
            id="distributor_bar_code"
            label="Штрих-код"
            value={draft.distributor_bar_code}
            onChange={(value) => patchDraft({ distributor_bar_code: value })}
          />
          <TextField
            id="distributor_ean_code"
            label="EAN"
            value={draft.distributor_ean_code}
            onChange={(value) => patchDraft({ distributor_ean_code: value })}
          />
          <SelectField
            id="distributor_unit"
            label="Ед.изм."
            options={UNIT_OPTIONS}
            value={draft.distributor_unit}
            onChange={(value) => patchDraft({ distributor_unit: value })}
          />
          <SwitchField
            id="is_active"
            label="Активен"
            value={draft.is_active}
            onChange={(value) => patchDraft({ is_active: value })}
          />
          <Txt fontWeight={600} mt={2}>Сопоставление с SKU</Txt>
          <Box
            borderWidth={1}
            borderColor="gray.200"
            borderRadius="md"
            p={3}
            bg="gray.50"
          >
            {selectedSku ? (
              <Flex direction="column" gap={1}>
                <Txt><b>ID:</b> {selectedSku.id}</Txt>
                <Txt><b>Название:</b> {selectedSku.title}</Txt>
                <Txt><b>Код ERP:</b> {selectedSku.erp_code || '—'}</Txt>
                <Txt><b>Код:</b> {selectedSku.code || '—'}</Txt>
                <Txt><b>Ед.изм:</b> {unitsById[selectedSku.unit_id] || '—'}</Txt>
                <Button alignSelf="flex-start" size="sm" mt={2} onClick={onClearMatch}>
                  Снять сопоставление
                </Button>
              </Flex>
            ) : (
              <Txt color="gray.500">Не сопоставлено</Txt>
            )}
          </Box>
        </Flex>

        <Flex flex={1.2} direction="column" gap={3} minW={0}>
          <Txt fontWeight={600}>Поиск SKU</Txt>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
            <TextField
              id="search_bar_code"
              label="Штрих-код"
              value={search.bar_code}
              onChange={(value) => patchSearch({ bar_code: value })}
            />
            <TextField
              id="search_erp_code"
              label="Код ERP"
              value={search.erp_code}
              onChange={(value) => patchSearch({ erp_code: value })}
            />
            <TextField
              id="search_code"
              label="Код"
              value={search.code}
              onChange={(value) => patchSearch({ code: value })}
            />
            <TextField
              id="search_title"
              label="Название"
              value={search.title}
              onChange={(value) => patchSearch({ title: value })}
            />
            <SelectField
              id="search_category"
              label="Категория"
              options={categories.map((item) => ({ id: item.id, name: item.name }))}
              value={search.category}
              onChange={(value) => patchSearch({ category: value, subcategory: undefined })}
            />
            <SelectField
              id="search_subcategory"
              label="Подкатегория"
              options={subcategoryOptions}
              value={search.subcategory}
              onChange={(value) => patchSearch({ subcategory: value })}
            />
          </SimpleGrid>
          <Button
            alignSelf="flex-start"
            colorScheme="brand"
            variant="outline"
            onClick={onSearch}
            isLoading={searchLoading}
          >
            Найти
          </Button>

          <Box maxH="320px" overflow="auto" mx={-2}>
            <TableBase<Sku>
              title=""
              data={foundSkus}
              columns={foundColumns}
              loading={searchLoading}
              paper={false}
            />
          </Box>
        </Flex>
      </Flex>
    </Dialog>
  );
};
